import { Pipette } from 'lucide-react'
import { useCallback, useId, useMemo } from 'react'
import { HexColorPicker } from 'react-colorful'

import {
  RESUME_COLOR_PRESETS,
  type ResumeColorPreference,
  type ResumeColorPresetId,
  resolveResumeColors,
} from '../../types/resumeColorScheme'

import styles from './ResumeAccentColorPicker.module.css'

interface ResumeAccentColorPickerProps {
  preference: ResumeColorPreference
  onChange: (preference: ResumeColorPreference) => void
}

function supportsEyeDropper(): boolean {
  return typeof window !== 'undefined' && 'EyeDropper' in window
}

export default function ResumeAccentColorPicker({
  preference,
  onChange,
}: ResumeAccentColorPickerProps) {
  const hexInputId = useId()
  const resolved = resolveResumeColors(preference)
  const hexValue = resolved.accentHex.replace(/^#/, '')
  const eyeDropperAvailable = useMemo(() => supportsEyeDropper(), [])

  const selectPreset = useCallback(
    (presetId: Exclude<ResumeColorPresetId, 'custom'>) => {
      const preset = RESUME_COLOR_PRESETS.find((item) => item.id === presetId)
      if (!preset) return
      onChange({ presetId, customHex: preset.accentHex })
    },
    [onChange]
  )

  const onCustomHexChange = useCallback(
    (hex: string) => {
      onChange({ presetId: 'custom', customHex: hex })
    },
    [onChange]
  )

  const onHexInputChange = useCallback(
    (raw: string) => {
      const next = raw.startsWith('#') ? raw : `#${raw}`
      if (/^#[0-9a-fA-F]{0,6}$/.test(next) && /^#[0-9a-fA-F]{6}$/.test(next)) {
        onCustomHexChange(next.toLowerCase())
      }
    },
    [onCustomHexChange]
  )

  const pickFromScreen = useCallback(async () => {
    if (!supportsEyeDropper()) return
    try {
      const EyeDropperCtor = (window as Window & { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } })
        .EyeDropper
      const result = await new EyeDropperCtor().open()
      onCustomHexChange(result.sRGBHex.toLowerCase())
    } catch {
      /* user cancelled */
    }
  }, [onCustomHexChange])

  return (
    <div className={styles.picker} data-testid="resume-accent-color-picker">
      <div className={styles.spectrum} data-testid="resume-color-picker-spectrum">
        <HexColorPicker
          color={resolved.accentHex}
          onChange={onCustomHexChange}
          className={styles.colorful}
        />
      </div>

      <div className={styles.hexRow}>
        <span
          className={styles.hexSwatch}
          style={{ backgroundColor: resolved.accentHex }}
          aria-hidden
          data-testid="resume-color-hex-preview"
        />
        <div className={styles.hexInputWrap}>
          <span className={styles.hexPrefix} aria-hidden>
            #
          </span>
          <input
            id={hexInputId}
            type="text"
            inputMode="text"
            spellCheck={false}
            autoComplete="off"
            aria-label="Hex"
            value={hexValue}
            onChange={(event) => onHexInputChange(event.target.value)}
            className={styles.hexInput}
            data-testid="resume-color-hex-input"
          />
        </div>
        {eyeDropperAvailable && (
          <button
            type="button"
            className={styles.eyedropper}
            title="Pick color from screen"
            aria-label="Pick color from screen"
            data-testid="resume-color-eyedropper"
            onClick={() => {
              void pickFromScreen()
            }}
          >
            <Pipette className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      <div className={styles.presetsSection}>
        <div className={styles.presetsLabel}>Presets</div>
        <div className={styles.swatches}>
          {RESUME_COLOR_PRESETS.map((preset) => {
            const active = preference.presetId === preset.id
            return (
              <button
                key={preset.id}
                type="button"
                title={preset.label}
                aria-label={preset.label}
                aria-pressed={active}
                data-testid={`resume-color-preset-${preset.id}`}
                className={`${styles.swatch} ${active ? styles.swatchActive : ''}`}
                style={{ backgroundColor: preset.accentHex }}
                onClick={() => selectPreset(preset.id)}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
